import { StyleSheet } from 'react-native';
import { C, FONT, R, SP } from './tokens';

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '').trim();
  const full = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized;
  const value = parseInt(full, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Baseline visual language used across overview + category screens.
 * Aligned with the design tokens in `./tokens.ts`.
 */
export const screenBackground = C.bg;

export const baseScreenStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: SP.md,
    backgroundColor: C.bg,
  },
  headerContainer: {
    marginBottom: SP.md,
  },
  header: {
    backgroundColor: 'transparent',
  },
  headerGradient: {
    backgroundColor: 'transparent',
    paddingVertical: SP.xs,
  },
  headerTitle: {
    fontFamily: FONT.sansBold,
    fontSize: 26,
    color: C.ink,
    letterSpacing: -0.3,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.ink2,
    lineHeight: 20,
  },
  section: {
    marginBottom: SP.md,
  },
  sectionTitle: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
  liquidCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
  },
  cardContent: {
    padding: SP.md,
  },
});

/**
 * Legacy helper retained for per-calc colors on older un-migrated screens.
 * Prefer tokens (`C`, `FONT`, `R`, `SP`) for new work.
 */
export function makeCalculatorStyles(accentHex: string) {
  return StyleSheet.create({
    ...baseScreenStyles,

    formulaContainer: {
      backgroundColor: rgba(accentHex, 0.1),
      padding: SP.md,
      borderRadius: R.card,
      marginBottom: SP.md,
      borderWidth: 1,
      borderColor: rgba(accentHex, 0.2),
    },
    formulaTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: accentHex,
      marginBottom: 8,
    },
    formulaText: {
      fontSize: 18,
      fontWeight: 'bold',
      color: C.ink,
      marginBottom: 6,
    },
    formulaDescription: {
      fontSize: 14,
      color: C.ink2,
    },
    inputRow: { marginBottom: SP.md },
    input: { backgroundColor: C.panel },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: SP.md,
    },
    button: {
      flex: 1,
      marginRight: 8,
      backgroundColor: accentHex,
    },
    buttonLabel: { fontSize: 16, fontWeight: 'bold' },
    clearButton: {
      flex: 1,
      marginLeft: 8,
      borderColor: accentHex,
    },
    clearButtonLabel: { fontSize: 16, color: accentHex },
    resultContainer: {
      backgroundColor: rgba(accentHex, 0.1),
      padding: SP.md,
      borderRadius: R.card,
      borderWidth: 1,
      borderColor: rgba(accentHex, 0.2),
    },
    resultLabel: {
      fontSize: 16,
      fontWeight: 'bold',
      color: accentHex,
      marginBottom: 8,
    },
    resultValue: { fontSize: 16, color: C.ink, marginBottom: 4 },
    noteContainer: {
      backgroundColor: C.panel2,
      padding: 14,
      borderRadius: R.field,
      borderWidth: 1,
      borderColor: C.edge,
    },
    noteTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 6,
      color: C.ink,
    },
    noteText: { fontSize: 13, color: C.ink2 },
  });
}
